import { useState, useMemo, useEffect } from "react";
import {
  Input,
  Select,
  Drawer,
  Timeline,
  Tag,
  Row,
  Col,
  Typography,
  Empty,
  Button,
  Divider,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  HistoryOutlined,
  RightOutlined,
  BankOutlined,
} from "@ant-design/icons";

import styles from "./IndoorRecords.module.css";
import { GetIndoorRecords } from "../../APIManager";

const { Title, Text } = Typography;
const { Option } = Select;

const IndoorRecords = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [schoolFilter, setSchoolFilter] = useState(null);
  const [sortOption, setSortOption] = useState("year_desc");

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [recordsData, setRecordsData] = useState({
    CurrentRecords: [],
    GroupedRecords: {},
  });

  const [loading, setLoading] = useState(true);

  /* =======================
     Fetch Records
     ======================= */
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        const res = await GetIndoorRecords();

        setRecordsData({
          CurrentRecords: res.data.CurrentRecords || [],
          GroupedRecords: res.data.GroupedRecords || {},
        });
      } catch (err) {
        console.error("Failed to fetch indoor records", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  /* =======================
     Helpers
     ======================= */
  const schools = useMemo(() => {
    const unique = new Set(recordsData.CurrentRecords.map((r) => r.SchoolName));
    return Array.from(unique);
  }, [recordsData.CurrentRecords]);

  const processedRecords = useMemo(() => {
    let records = [...recordsData.CurrentRecords];

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      records = records.filter(
        (r) =>
          r.EventName.toLowerCase().includes(lower) ||
          r.RowerName.toLowerCase().includes(lower)
      );
    }

    if (schoolFilter) {
      records = records.filter((r) => r.SchoolName === schoolFilter);
    }

    records.sort((a, b) => {
      if (sortOption === "year_desc") return Number(b.Year) - Number(a.Year);
      if (sortOption === "year_asc") return Number(a.Year) - Number(b.Year);
      if (sortOption === "time_asc") return a.Time.localeCompare(b.Time);
      return 0;
    });

    return records;
  }, [searchTerm, schoolFilter, sortOption, recordsData.CurrentRecords]);

  const getNormalizedHistory = (eventName) => {
    const raw = recordsData.GroupedRecords[eventName] || [];
    const map = new Map();

    raw.forEach((r) => {
      const key = `${r.Year}-${r.Time}-${r.RowerName}-${r.SchoolName}`;
      if (!map.has(key)) map.set(key, r);
    });

    return Array.from(map.values()).sort(
      (a, b) => Number(b.Year) - Number(a.Year)
    );
  };

  const handleCardClick = (record) => {
    setSelectedRecord(record);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => setSelectedRecord(null), 300);
  };

  /* =======================
     Render
     ======================= */
  return (
    <>
      {/* ================= Drawer ================= */}
      <Drawer
        title={
          <div className={styles.ir_drawerTitle}>
            <HistoryOutlined />
            <span>Record History</span>
          </div>
        }
        placement="right"
        visible={drawerVisible}
        onClose={closeDrawer}
        width={420}
        destroyOnClose
      >
        {selectedRecord && (
          <div className={styles.ir_drawerContent}>
            <div className={styles.ir_drawerHeaderInfo}>
              <Title level={4} style={{ margin: 0 }}>
                {selectedRecord.EventName}
              </Title>
              <Text type="secondary">
                Current Record:{" "}
                <strong style={{ color: "#faad14" }}>
                  {selectedRecord.Time}
                </strong>
              </Text>
            </div>

            <Timeline mode="left">
              {getNormalizedHistory(selectedRecord.EventName).length > 0 ? (
                getNormalizedHistory(selectedRecord.EventName).map(
                  (hist, index) => (
                    <Timeline.Item
                      key={hist._id}
                      color={index === 0 ? "green" : "gray"}
                      label={
                        <span className={styles.ir_timelineYear}>
                          {hist.Year}
                        </span>
                      }
                    >
                      <div className={styles.ir_timelineCard}>
                        <div className={styles.ir_timelineTime}>
                          {hist.Time}
                        </div>

                        <div className={styles.ir_timelineSchool}>
                          <BankOutlined />
                          <span>{hist.SchoolName}</span>
                        </div>

                        <div className={styles.ir_timelineRowers}>
                          <TeamOutlined />
                          <span>{hist.RowerName}</span>
                        </div>
                      </div>
                    </Timeline.Item>
                  )
                )
              ) : (
                <Timeline.Item color="gray">
                  No historical data available.
                </Timeline.Item>
              )}
            </Timeline>
          </div>
        )}
      </Drawer>

      {/* ================= Page ================= */}
      <div className={styles.ir_pageContainer}>
        <div className={styles.ir_headerSection}>
          <Divider>
            {" "}
            <Title level={3}>Indoor Records</Title>
          </Divider>
          <div className={styles.ir_controlBar}>
            {" "}
            <Input
              placeholder="Search Event or Rower..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              className={styles.ir_searchInput}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />{" "}
            <div className={styles.ir_filtersWrapper}>
              {" "}
              <Select
                placeholder="Filter by School"
                allowClear
                className={styles.ir_filterSelect}
                onChange={setSchoolFilter}
                suffixIcon={<FilterOutlined />}
              >
                {" "}
                {schools.map((school) => (
                  <Option key={school} value={school}>
                    {" "}
                    {school}{" "}
                  </Option>
                ))}{" "}
              </Select>{" "}
              <Select
                defaultValue="year_desc"
                className={styles.ir_sortSelect}
                onChange={setSortOption}
              >
                {" "}
                <Option value="year_desc">Newest First</Option>{" "}
                <Option value="year_asc">Oldest First</Option>{" "}
                <Option value="time_asc">Fastest Time</Option>{" "}
              </Select>{" "}
            </div>{" "}
          </div>{" "}
        </div>

        <div className={styles.ir_gridContainer}>
          {" "}
          {processedRecords.length > 0 ? (
            <Row gutter={[24, 24]}>
              {" "}
              {processedRecords.map((record) => (
                <Col xs={24} sm={12} lg={8} key={record._id}>
                  {" "}
                  <div
                    className={`${styles.ir_recordCard} ${
                      record.Year === "2025"
                        ? styles.ir_currentYearHighlight
                        : ""
                    }`}
                  >
                    {" "}
                    <div className={styles.ir_cardHeader}>
                      {" "}
                      <Text strong className={styles.ir_eventName}>
                        {" "}
                        {record.EventName}{" "}
                      </Text>{" "}
                      <Tag color="gold" className={styles.ir_yearTag}>
                        {" "}
                        {record.Year}{" "}
                      </Tag>{" "}
                    </div>{" "}
                    {/* MOVED: School Name now higher in hierarchy */}{" "}
                    <div className={styles.ir_schoolHeader}>
                      {" "}
                      <BankOutlined
                        className={styles.ir_schoolIconSmall}
                      />{" "}
                      <Text className={styles.ir_schoolNameHeader} ellipsis>
                        {" "}
                        {record.SchoolName}{" "}
                      </Text>{" "}
                    </div>{" "}
                    <div className={styles.ir_heroStat}>
                      <div>
                        {" "}
                        <ClockCircleOutlined
                          className={styles.ir_timeIcon}
                        />{" "}
                        <span className={styles.ir_timeValue}>
                          {record.Time}
                        </span>{" "}
                      </div>
                    </div>{" "}
                    <div className={styles.ir_athletesSection}>
                      {" "}
                      <div className={styles.ir_sectionLabel}>
                        {" "}
                        <TeamOutlined
                          style={{ fontSize: "12px" }}
                        /> Rower(s){" "}
                      </div>{" "}
                      {/* CHANGED: Text list instead of Tags */}{" "}
                      <div className={styles.ir_athleteText}>
                        {" "}
                        {record.RowerName}{" "}
                      </div>{" "}
                    </div>{" "}
                    <div className={styles.ir_cardFooter}>
                      {" "}
                      <Button
                        type="link"
                        size="small"
                        className={styles.ir_historyBtn}
                        icon={<RightOutlined />}
                        onClick={() => handleCardClick(record)}
                      >
                        {" "}
                        View History{" "}
                      </Button>{" "}
                    </div>{" "}
                  </div>{" "}
                </Col>
              ))}{" "}
            </Row>
          ) : (
            <div className={styles.ir_emptyState}>
              {" "}
              <Empty description="No records found matching your criteria" />{" "}
            </div>
          )}{" "}
        </div>
      </div>
    </>
  );
};

export default IndoorRecords;
